import React from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Text, useMultiStyleConfig, useClipboard, Link, Button} from '@chakra-ui/react'
import {CopyIcon} from '../icons'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

const ProductDetailBanner = ({
    bannerTitle,
    bannerTag,
    bannerDescription,
    bannerImageDesktop,
    bannerImageMobile,
    bannerLabelLink,
    bannerLink,
    bannerCode,
    onFocusLinkClick,
}) => {
    const styles = useMultiStyleConfig('ProductDetailBanner')
    const {onCopy, hasCopied} = useClipboard(bannerCode || '')
    const {locale} = useMultiSite()
    let linkElement = null

    if (bannerLink && bannerLabelLink) {
        const isFocusLink = bannerLink.startsWith('focus=')

        if (isFocusLink) {
            // Focus Link (to enable scrolling)
            const focusTarget = bannerLink.split('=')[1]?.trim()

            if (focusTarget && onFocusLinkClick) {
                linkElement = (
                    <Button
                        variant="text-button"
                        onClick={() => onFocusLinkClick(focusTarget)}
                        {...styles.bannerLink}
                    >
                        {bannerLabelLink}
                    </Button>
                )
            }
        } else {
            // External or Internal link
            const isAbsolute = !(bannerLink.startsWith('/') && !bannerLink.startsWith('//'))
            const internalLink = !isAbsolute ? `/${locale?.alias || ''}${bannerLink}` : bannerLink

            linkElement = (
                <Link href={internalLink} isExternal={isAbsolute} {...styles.bannerLink}>
                    {bannerLabelLink}
                </Link>
            )
        }
    }

    return (
        <Box {...styles.container} data-testid="product-detail-banner">
            <Flex {...styles.flexContainer}>
                <Flex {...styles.textContent}>
                    {bannerTag && (
                        <Box {...styles.tagContainer} data-testid="product-detail-banner-tag">
                            <Text {...styles.tagText}>{bannerTag}</Text>
                        </Box>
                    )}
                    <Flex {...styles.textContainer}>
                        <Text {...styles.title}>{bannerTitle}</Text>
                        <Text {...styles.description}>{bannerDescription}</Text>
                        {linkElement}
                    </Flex>
                    {bannerCode && (
                        <Flex
                            {...styles.codeContainer}
                            color={hasCopied ? 'text.success' : 'text.primary'}
                            borderColor={hasCopied ? 'text.success' : 'border.primary'}
                            data-testid="product-detail-banner-code"
                        >
                            <Button {...styles.codeText} onClick={onCopy}>
                                {bannerCode}
                            </Button>
                            <CopyIcon {...styles.copyIcon} />
                        </Flex>
                    )}
                </Flex>
                {(bannerImageDesktop || bannerImageMobile) && (
                    <Box {...styles.imageContent}>
                        <picture style={{width: '100%', height: '100%'}}>
                            {bannerImageDesktop && (
                                <source srcSet={bannerImageDesktop} media="(min-width: 1024px)" />
                            )}
                            <img
                                src={bannerImageMobile || bannerImageDesktop}
                                alt={bannerTitle}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                }}
                            />
                        </picture>
                    </Box>
                )}
            </Flex>
        </Box>
    )
}

ProductDetailBanner.propTypes = {
    bannerTitle: PropTypes.string,
    bannerTag: PropTypes.string,
    bannerDescription: PropTypes.string,
    bannerImageDesktop: PropTypes.string,
    bannerImageMobile: PropTypes.string,
    bannerLink: PropTypes.string,
    bannerCode: PropTypes.string,
    bannerLabelLink: PropTypes.string,
    onFocusLinkClick: PropTypes.func,
}

export default ProductDetailBanner
